/**
 * Utility functions for invoice management and payment terms parsing
 */

export interface ParsedInstallment {
  percentage: number;
  dueDate: string | null;
  description: string;
}

/**
 * Parse payment terms text into structured installments
 * Example: "20% to confirm, 30% in July 2026, 50% in November 2026"
 */
export function parsePaymentTerms(paymentTerms: string | null): ParsedInstallment[] {
  if (!paymentTerms) return [];

  // Check for natural language "full payment" patterns first
  const fullPaymentPatterns = [
    /in\s+full\s+(?:in\s+)?advance/i,
    /in\s+advance\s+in\s+full/i,           // handles "in advance in full"
    /due\s+in\s+(?:advance\s+in\s+)?full/i, // handles "due in full" or "due in advance in full"
    /full\s+payment\s+upfront/i,
    /full\s+payment\s+(?:is\s+)?due/i,
    /payment\s+in\s+full/i,
    /full\s+amount/i,
    /100%?\s*upfront/i,
    /100%?\s*deposit/i,
  ];

  for (const pattern of fullPaymentPatterns) {
    if (pattern.test(paymentTerms)) {
      return [{
        percentage: 100,
        dueDate: null, // Immediate payment
        description: paymentTerms.trim(),
      }];
    }
  }

  const installments: ParsedInstallment[] = [];
  
  // Split by comma or semicolon
  const parts = paymentTerms.split(/[,;\n•]/).map(p => p.trim());
  
  for (const part of parts) {
    // Extract percentage (look for number followed by %)
    const percentageMatch = part.match(/(\d+(?:\.\d+)?)\s*%/);
    if (!percentageMatch) continue;
    if (/refund|cancel|forfeit|penalt/i.test(part)) continue;
    
    const percentage = parseFloat(percentageMatch[1]);
    
    // Try to extract date information
    let dueDate: string | null = null;
    
    // Look for month and year (e.g., "July 2026", "in July 2026")
    const monthYearMatch = part.match(/(?:in\s+)?([A-Za-z]+)\s+(\d{4})/i);
    if (monthYearMatch) {
      const [, month, year] = monthYearMatch;
      const monthIndex = new Date(`${month} 1, 2000`).getMonth();
      if (!isNaN(monthIndex)) {
        // Set to first day of the month
        dueDate = `${year}-${String(monthIndex + 1).padStart(2, '0')}-01`;
      }
    }
    
    // Look for specific dates (e.g., "on 15/07/2026", "by 2026-07-15")
    const specificDateMatch = part.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
    if (specificDateMatch && !dueDate) {
      const [, day, month, year] = specificDateMatch;
      dueDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    installments.push({
      percentage,
      dueDate,
      description: part,
    });
  }
  
  const total = installments.reduce((a, i) => a + i.percentage, 0);
  if (installments.length === 0 || total > 100.01) {
    return [{ percentage: 100, dueDate: null, description: "Full payment (no instalment split found in terms)" }];
  }
  return installments;
}

/**
 * Calculate invoice amount based on percentage and total
 */
export function calculateInvoiceAmount(
  percentage: number,
  totalAmount: number
): number {
  return (totalAmount * percentage) / 100;
}

/**
 * Generate invoice status summary text
 * Example: "3 Invoices Due: First Invoice Issued"
 */
export function generateInvoiceStatusSummary(
  invoices: Array<{ installment_number: number; status: string }>
): string {
  const total = invoices.length;
  const paidCount = invoices.filter(i => i.status === 'paid').length;
  const issuedCount = invoices.filter(i => i.status === 'issued').length;
  
  if (paidCount === total) {
    return `All ${total} Invoices Paid`;
  }
  
  // Find first non-paid invoice
  const sortedInvoices = [...invoices].sort((a, b) => a.installment_number - b.installment_number);
  const firstUnpaid = sortedInvoices.find(i => i.status !== 'paid');
  
  if (!firstUnpaid) {
    return `${total} Invoices Due`;
  }
  
  const ordinal = getOrdinal(firstUnpaid.installment_number);
  const statusText = firstUnpaid.status === 'issued' ? 'Issued' : 'Pending';
  
  return `${total} Invoices Due: ${ordinal} Invoice ${statusText}`;
}

/**
 * Get ordinal suffix for numbers (1st, 2nd, 3rd, etc.)
 */
function getOrdinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/**
 * Format currency amount
 */
export function formatInvoiceAmount(amount: number, currency: string = 'USD'): string {
  const symbols: Record<string, string> = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    ZAR: 'R',
  };
  
  const symbol = symbols[currency] || currency;
  return `${symbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Allowed shortfall reasons when amount_received < invoiced amount.
 */
export const SHORTFALL_REASONS: { value: string; label: string }[] = [
  { value: 'bank_transfer_fees', label: 'Bank transfer fees' },
  { value: 'incorrect_payment', label: 'Incorrect payment' },
  { value: 'currency_conversion', label: 'Currency conversion loss' },
  { value: 'other', label: 'Other' },
];

export function getShortfallReasonLabel(value: string | null | undefined): string {
  if (!value) return '';
  return SHORTFALL_REASONS.find(r => r.value === value)?.label || value;
}

export function getShortfall(amount: number, received: number | null | undefined): number {
  if (received === null || received === undefined) return 0;
  const diff = amount - received;
  return diff > 0.0049 ? diff : 0;
}
